import { useCallback, useEffect, useRef } from 'react';

/** Задержка поиска по умолчанию: запрос уходит, когда админ перестал печатать. */
export const SEARCH_DEBOUNCE_MS = 300;

/**
 * Возвращает функцию, которая вызывает `fn` через `delay` мс после последнего вызова.
 * Для полей поиска: значение в инпуте обновляется сразу, запрос (и сброс страницы) — с задержкой.
 */
export function useDebouncedCallback<A extends unknown[]>(
  fn: (...args: A) => void,
  delay: number = SEARCH_DEBOUNCE_MS,
): (...args: A) => void {
  const fnRef = useRef(fn);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return useCallback(
    (...args: A) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fnRef.current(...args), delay);
    },
    [delay],
  );
}
